import React, { useState } from 'react';
import { CheckCircle2, Clock, Loader2, Ban } from 'lucide-react';
import styles from '../Vendors.module.css';
import { vendorFetch, errText, inr2, fmtDate, modeLabel, STATUS } from '../../../utils/vendorApi';

export const StatusPill = ({ bill }) => {
    if (bill.overdue) return <span className={`${styles.pill} ${styles.pill_red}`}>Overdue</span>;
    const s = STATUS[bill.status] || STATUS.UNPAID;
    return <span className={`${styles.pill} ${styles[`pill_${s.tone}`]}`}>{s.label}</span>;
};

/** Purchase bills. onPay(bill), onCancel(bill), onReturn(bill) are optional */
export function BillsTable({ bills, showVendor = true, onPay, onCancel, onReturn, onOpenVendor, emptyText = 'No purchase bills yet.' }) {
    if (!bills.length) return <div className={styles.empty}>{emptyText}</div>;
    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Bill date</th>{showVendor && <th>Vendor</th>}<th>Bill</th><th className={styles.right}>Total</th>
                        <th className={styles.right}>Paid</th><th className={styles.right}>Left to pay</th><th>Due</th><th>Status</th><th />
                    </tr>
                </thead>
                <tbody>
                    {bills.map((b) => (
                        <tr key={b._id} className={b.status === 'CANCELLED' ? styles.rowVoid : ''}>
                            <td className={styles.num}>{fmtDate(b.billDate)}</td>
                            {showVendor && <td><button type="button" className={styles.btnGhost} style={{ padding: 0, height: 'auto' }} onClick={() => onOpenVendor?.(b.vendorId)}>{b.vendorName}</button></td>}
                            <td>
                                <span className={styles.stack}>
                                    <b>{b.billNo || b.refNo}</b>
                                    <small>{b.items?.length || 0} products{b.hasGst ? ` · GST ${inr2(b.gstTotal)}` : ''}{b.returned > 0 ? ` · returned ${inr2(b.returned)}` : ''}</small>
                                </span>
                            </td>
                            <td className={`${styles.right} ${styles.num}`}>{inr2(b.total)}</td>
                            <td className={`${styles.right} ${styles.num}`}>{inr2(b.paid)}</td>
                            <td className={`${styles.right} ${styles.num} ${b.balance > 0.01 ? styles.red : ''}`}>{b.status === 'CANCELLED' ? '—' : inr2(b.balance)}</td>
                            <td className={`${styles.num} ${b.overdue ? styles.red : ''}`}>{b.dueDate ? fmtDate(b.dueDate) : '—'}</td>
                            <td><StatusPill bill={b} /></td>
                            <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {onPay && b.balance > 0.01 && b.status !== 'CANCELLED' && <button type="button" className={`${styles.btnPrimary} ${styles.small}`} onClick={() => onPay(b)}>Pay</button>}
                                {onReturn && b.status !== 'CANCELLED' && <button type="button" className={styles.btnGhost} onClick={() => onReturn(b)}>Return</button>}
                                {onCancel && b.status !== 'CANCELLED' && b.paid <= 0.01 && b.returned <= 0.01 && <button type="button" className={styles.btnGhost} style={{ color: 'var(--h-color-danger-600, #be1025)' }} onClick={() => onCancel(b)}>Cancel</button>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Cleared / not cleared button: anyone with vendors.clear_payment can switch it */
export function ClearToggle({ payment, storeId, canClear, onChanged }) {
    const [busy, setBusy] = useState(false);
    const yes = payment.clearStatus === 'CLEARED';
    const flip = async () => {
        if (!canClear || payment.voided) return;
        setBusy(true);
        try {
            const json = await vendorFetch(`/payments/${payment._id}/clear`, { method: 'PATCH', storeId, body: { cleared: !yes } });
            onChanged?.(json.data, json.message);
        } catch (e) { onChanged?.(null, errText(e), true); } finally { setBusy(false); }
    };
    return (
        <button type="button" className={`${styles.clearBtn} ${yes ? styles.clearYes : styles.clearNo}`} onClick={flip} disabled={!canClear || busy || payment.voided}
            title={canClear ? (yes ? `Cleared${payment.clearedByName ? ` by ${payment.clearedByName}` : ''}. Click if it bounced or is still in process.` : 'Click when the money has left your account.') : ''}>
            {busy ? <Loader2 size={11} className={styles.spin} /> : yes ? <CheckCircle2 size={11} /> : <Clock size={11} />}
            {yes ? 'Cleared' : 'Not cleared'}
        </button>
    );
}

export function PaymentsTable({ payments, storeId, showVendor = true, canClear, canVoid, onChanged, onVoid, onOpenVendor, emptyText = 'No payments yet.' }) {
    if (!payments.length) return <div className={styles.empty}>{emptyText}</div>;
    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr><th>Date</th>{showVendor && <th>Vendor</th>}<th>Paid by</th><th className={styles.right}>Amount</th><th>Put on</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                    {payments.map((p) => (
                        <tr key={p._id} className={p.voided ? styles.rowVoid : ''}>
                            <td className={styles.num}>{fmtDate(p.date)}</td>
                            {showVendor && <td><button type="button" className={styles.btnGhost} style={{ padding: 0, height: 'auto' }} onClick={() => onOpenVendor?.(p.vendorId)}>{p.vendorName}</button></td>}
                            <td><span className={styles.stack}><b>{modeLabel(p.mode)}</b><small>{p.reference || p.refNo}{p.createdByName ? ` · by ${p.createdByName}` : ''}</small></span></td>
                            <td className={`${styles.right} ${styles.num}`} style={{ fontWeight: 900 }}>{inr2(p.amount)}</td>
                            <td>
                                <span className={styles.stack}>
                                    <small>{p.allocations?.length ? p.allocations.map((a) => `${a.billNo || a.refNo} ${inr2(a.amount)}`).join(', ') : 'No bill'}</small>
                                    {p.unallocated > 0.009 && <small className={styles.green}>Advance left {inr2(p.unallocated)}</small>}
                                    {p.voided && <small className={styles.red}>Voided: {p.voidReason}</small>}
                                </span>
                            </td>
                            <td>{p.voided ? <span className={`${styles.pill} ${styles.pill_grey}`}><Ban size={10} /> Voided</span> : <ClearToggle payment={p} storeId={storeId} canClear={canClear} onChanged={onChanged} />}</td>
                            <td style={{ textAlign: 'right' }}>{canVoid && !p.voided && <button type="button" className={styles.btnGhost} style={{ color: 'var(--h-color-danger-600, #be1025)' }} onClick={() => onVoid?.(p)}>Void</button>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
